import { Outlet, Link, createRootRouteWithContext, HeadContent, Scripts, useRouterState } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/components/theme-provider";
import { PaymentsUnavailableDialog } from "@/components/payments-unavailable-dialog";
import { WhatsAppFab } from "@/components/whatsapp-fab";
import { SmoothScroll } from "@/components/vision/smooth-scroll";
import { installGlobalErrorHandlers } from "@/lib/error-logger";
import { NavbarDarkGuard } from "@/components/dev/navbar-dark-guard";
import "@/lib/i18n";
import { applyClientLang } from "@/lib/i18n";

import appCss from "../styles.css?url";

interface RouterContext {
  queryClient: QueryClient;
}

function NotFoundComponent() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-8xl font-bold gradient-text">404</h1>
        <h2 className="mt-4 font-display text-2xl font-semibold text-foreground">{t("notFound.title")}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("notFound.desc")}
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t("notFound.cta")}
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "author", content: "LexAIPro" },
      { name: "google-site-verification", content: "iJ_f_XZa-4yaLxRUsxALx529GXghh52mOamqHqUJhIE" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:site_name", content: "LexAIPro" },
      { property: "og:locale", content: "it_IT" },
      { name: "theme-color", content: "#ffffff" },
      { name: "color-scheme", content: "light" },
      { name: "format-detection", content: "telephone=no" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "LexAIPro" },
      { name: "theme-color", content: "#ffffff", media: "(prefers-color-scheme: light)" },
      { name: "theme-color", content: "#000000", media: "(prefers-color-scheme: dark)" },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/44e38395-3a70-4a9f-865e-cde6416e3d89" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/44e38395-3a70-4a9f-865e-cde6416e3d89" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      // SVG favicon adapts to light/dark via prefers-color-scheme inside the SVG.
      { rel: "icon", href: "/favicon.svg", type: "image/svg+xml", sizes: "any" },
      // Light/dark fallbacks for browsers that honor media on <link rel="icon">.
      { rel: "icon", href: "/favicon-dark.svg", type: "image/svg+xml", media: "(prefers-color-scheme: dark)" },
      // iOS home screen icons (light + dark).
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon-dark.png", media: "(prefers-color-scheme: dark)" },
      // Safari pinned-tab monochrome.
      { rel: "mask-icon", href: "/safari-pinned-tab.svg", color: "#0a0a0a" },
      // Self-hosted font preload for the LCP heading (Inter Tight). Eliminates
      // the third-party render-blocking Google Fonts CSS chain.
      {
        rel: "preload",
        href: "/fonts/InterTight-lat.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      // Editorial display face (Playfair Display) for the marketing hero headline.
      {
        rel: "preload",
        href: "/fonts/PlayfairDisplay-lat.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="it">
      <head>
        <HeadContent />
        {/* Pre-hydration recovery: if the client bundle fails to load (dev 504 / chunk error),
            reload the page once so the user never sees an unstyled HTML fallback. */}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){try{var K='__lex_boot_reload';var booted=false;function ok(){booted=true;}window.addEventListener('error',function(e){if(booted)return;var t=e&&e.target;if(t&&(t.tagName==='SCRIPT'||t.tagName==='LINK')){if(!sessionStorage.getItem(K)){sessionStorage.setItem(K,'1');location.reload();}}},true);setTimeout(function(){if(booted)return;if(!window.__TSR_ROUTER__&&!document.querySelector('[data-hydrated]')){if(!sessionStorage.getItem(K)){sessionStorage.setItem(K,'1');location.reload();}}},5000);setTimeout(function(){try{sessionStorage.removeItem(K);}catch(_){}},20000);window.__lex_booted=ok;}catch(_){}})();`,
          }}
        />
      </head>
      <body className="no-overscroll ios-tap-highlight-none">
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function HtmlLangSync() {
  const { i18n } = useTranslation();
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = i18n.resolvedLanguage?.startsWith("en") ? "en" : "it";
    }
  }, [i18n.resolvedLanguage]);
  return null;
}

function RouteTransition() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <div key={pathname} className="md:!animate-none ios-route-enter">
      <Outlet />
    </div>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useEffect(() => {
    installGlobalErrorHandlers();
    try { (window as any).__lex_booted?.(); document.documentElement.setAttribute("data-hydrated", "1"); } catch {}
    // Switch language only once hydration has fully settled, otherwise
    // lazily hydrated subtrees re-render mid-hydration and React warns.
    let timer = 0;
    const run = () => { timer = window.setTimeout(() => applyClientLang(), 400); };
    if (document.readyState === "complete") run();
    else window.addEventListener("load", run, { once: true });
    return () => { window.clearTimeout(timer); window.removeEventListener("load", run); };
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <SmoothScroll />
        <HtmlLangSync />
        <RouteTransition />
        <NavbarDarkGuard />
        <PaymentsUnavailableDialog />
        <WhatsAppFab />
        <Toaster />
      </ThemeProvider>
    </QueryClientProvider>
  );
}
